import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Trash2, ShieldCheck, Clock, FileText, CheckCircle2, Loader2, ArrowLeft } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { apiErrorText } from '../lib/apiError';

const API = process.env.REACT_APP_BACKEND_URL;

const ACCOUNT_TYPES = [
  { value: 'operator', label: 'Operator / business owner' },
  { value: 'team_member', label: 'Team member' },
  { value: 'customer', label: 'Booking customer' },
];

export default function DeleteAccountPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', account_type: 'operator', org_name: '', details: '' });
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [ticketNumber, setTicketNumber] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || !form.email.trim()) { setError('Please enter your name and email.'); return; }
    if (!confirmed) { setError('Please confirm you understand what will be deleted.'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/v2/support/public/deletion-request`, form);
      setTicketNumber(res.data.ticket_number);
    } catch (err) {
      setError(apiErrorText(err, 'Could not submit your request — please try again or email support@bookingflows.com'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="delete-account-page">
      <Helmet>
        <title>Delete Your Account &amp; Data | BookingFlows</title>
        <meta name="description" content="Request deletion of your BookingFlows account and associated personal data. No login required." />
      </Helmet>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <button onClick={() => navigate('/')} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors mb-8" data-testid="delete-account-back-home">
          <ArrowLeft className="w-4 h-4" /> BookingFlows home
        </button>

        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 rounded-2xl bg-red-50 border border-red-100 flex items-center justify-center flex-shrink-0">
            <Trash2 className="w-5 h-5 text-red-500" />
          </div>
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">Delete your account &amp; data</h1>
            <p className="text-gray-500 mt-2 text-sm sm:text-base">
              This applies to the <strong className="text-gray-700">BookingFlows</strong> app and website. Anyone can request deletion here — no login required.
            </p>
          </div>
        </div>

        {/* What happens */}
        <div className="grid sm:grid-cols-3 gap-3 mb-8">
          {[
            { icon: ShieldCheck, title: 'We verify it\'s you', text: 'We confirm ownership via your account email before deleting anything — so no one can delete your data but you.' },
            { icon: Trash2, title: 'What gets deleted', text: 'Your login, profile (name, email, phone), 2FA methods, saved preferences — and, for business owners, your organization\'s data on request.' },
            { icon: Clock, title: 'Within 30 days', text: 'Verified requests are completed within 30 days. You\'ll get a confirmation email when it\'s done.' },
          ].map((c, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-2xl p-4">
              <c.icon className="w-4 h-4 text-blue-600 mb-2" />
              <div className="text-sm font-semibold text-gray-800">{c.title}</div>
              <p className="text-xs text-gray-500 mt-1 leading-relaxed">{c.text}</p>
            </div>
          ))}
        </div>

        {/* What we keep */}
        <div className="bg-white border border-gray-200 rounded-2xl p-5 mb-8" data-testid="delete-account-retention-note">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-4 h-4 text-gray-400" />
            <span className="text-sm font-semibold text-gray-800">Some records are kept where the law requires it</span>
          </div>
          <ul className="text-xs text-gray-500 leading-relaxed list-disc pl-5 space-y-1">
            <li>Completed booking &amp; payment records — kept up to 7 years for tax and accounting law, then erased</li>
            <li>Signed waivers — kept for the legal statute-of-limitations period in your jurisdiction</li>
            <li>Security &amp; audit logs — retained briefly for fraud prevention, then anonymized</li>
          </ul>
          <p className="text-xs text-gray-400 mt-2">
            Booked through a business that uses BookingFlows? That business controls your booking data — we'll route your request to them and assist. Full details in our{' '}
            <button onClick={() => navigate('/privacy')} className="text-blue-600 hover:underline" data-testid="delete-account-privacy-link">Privacy Policy</button>.
          </p>
        </div>

        {ticketNumber ? (
          <div className="bg-white border border-emerald-200 rounded-2xl p-8 text-center" data-testid="delete-account-success">
            <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
            <h2 className="text-xl font-bold text-gray-900">Request received</h2>
            <p className="text-sm text-gray-500 mt-2">
              Your deletion request <span className="font-mono font-semibold text-gray-700">{ticketNumber}</span> is in.
              We've emailed you a confirmation — we'll verify it's you, then complete the deletion within 30 days.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white border border-gray-200 rounded-2xl p-5 sm:p-6 space-y-4" data-testid="delete-account-form">
            <h2 className="text-lg font-bold text-gray-900">Submit a deletion request</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Full name</label>
                <Input value={form.name} onChange={set('name')} placeholder="Your name" maxLength={255} data-testid="delete-account-name-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Account email</label>
                <Input type="email" value={form.email} onChange={set('email')} placeholder="you@example.com" maxLength={255} data-testid="delete-account-email-input" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">I am a…</label>
                <select
                  value={form.account_type}
                  onChange={set('account_type')}
                  data-testid="delete-account-type-select"
                  className="w-full h-10 rounded-md border border-input bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {ACCOUNT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Business name <span className="text-gray-400">(optional)</span></label>
                <Input value={form.org_name} onChange={set('org_name')} placeholder="e.g. Ryan's Boat Company" maxLength={255} data-testid="delete-account-org-input" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Anything else we should know? <span className="text-gray-400">(optional)</span></label>
              <Textarea value={form.details} onChange={set('details')} rows={3} maxLength={4000} placeholder="e.g. delete only my personal profile, keep the business account" data-testid="delete-account-details-input" />
            </div>
            <label className="flex items-start gap-2.5 text-xs text-gray-600 cursor-pointer">
              <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} className="mt-0.5" data-testid="delete-account-confirm-checkbox" />
              <span>I understand this permanently deletes my account and personal data (except records we're legally required to keep), and it can't be undone.</span>
            </label>
            {error && <p className="text-sm text-red-600" data-testid="delete-account-error">{error}</p>}
            <button
              type="submit"
              disabled={submitting}
              data-testid="delete-account-submit-button"
              className="w-full sm:w-auto px-6 h-11 rounded-full bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white text-sm font-semibold transition-colors inline-flex items-center justify-center gap-2"
            >
              {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</>) : 'Request deletion'}
            </button>
          </form>
        )}

        <p className="text-xs text-gray-400 mt-6">
          Signed in already? You can also file this from the{' '}
          <button onClick={() => navigate('/support?category=Account%20%26%20Data%20Deletion#tickets')} className="text-blue-600 hover:underline" data-testid="delete-account-support-link">Support Center</button>{' '}
          under "Account &amp; Data Deletion", or email <a href="mailto:support@bookingflows.com" className="text-blue-600 hover:underline">support@bookingflows.com</a>.
        </p>
      </div>
    </div>
  );
}
